import http from './api'
import config from './config'
import { User } from '../types/user'
import { rememberInviteToken } from '../utils/inviteRedirect'

export interface SignInData {
  email: string
  password: string
}

export interface SignUpData {
  name: string
  email: string
  password: string
}

export interface AuthResponse {
  user: User
  token: string
}

class AuthService {
  async signIn(data: SignInData): Promise<AuthResponse> {
    const response = await http.post('/auth/sign-in', data)
    return response.data
  }

  async signUp(data: SignUpData): Promise<AuthResponse> {
    const response = await http.post('/auth/sign-up', data)
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await http.get('/auth/current-user')
    return response.data
  }

  async forgotPassword(email: string): Promise<{ message: string; resetLink?: string }> {
    const frontendUrl = typeof window !== 'undefined' ? window.location.origin : ''
    const response = await http.post<{ message: string; resetLink?: string }>(
      '/auth/forgot-password',
      { email, ...(frontendUrl && { frontendUrl }) },
      { timeout: 90000 }
    )
    return response.data
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await http.post<{ message: string }>('/auth/reset-password', {
      token,
      password,
    })
    return response.data
  }

  async signInWithGoogleIdToken(idToken: string): Promise<AuthResponse> {
    const response = await http.post('/auth/google', { idToken })
    return response.data
  }

  /**
   * Start Google on THIS site (breakroomcup.com / localhost), not the API host.
   * Google then shows “continue to breakroomcup.com” instead of DigitalOcean.
   */
  startGoogleSignIn(): void {
    const params = new URLSearchParams(window.location.search)
    rememberInviteToken(params.get('invite'))
    const clientId = config.googleClientId
    if (!clientId) {
      const redirectUri = `${window.location.origin}/auth/callback`
      window.location.href = `${config.baseURL}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`
      return
    }
    const redirectUri = `${window.location.origin}/auth/callback`
    const nonce =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('google_oauth_nonce', nonce)
    const authParams = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
    })
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${authParams.toString()}`
  }

  logout(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('authData')
  }

  /**
   * Decode the JWT exp claim without any library.
   * Returns expiry as ms timestamp, or null if unreadable.
   */
  private getTokenExpiry(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null
    } catch {
      return null
    }
  }

  isTokenValid(): boolean {
    const token = this.getToken()
    if (!token) return false

    const expiry = this.getTokenExpiry(token)
    // If we can't read the expiry, fall back to presence check (server will reject if invalid)
    if (expiry === null) return true

    if (Date.now() >= expiry) {
      this.logout()
      return false
    }

    return true
  }

  restoreAuth(): { user: User | null; token: string | null } {
    if (!this.isTokenValid()) {
      return { user: null, token: null }
    }

    const user = this.getUser()
    const token = this.getToken()

    return { user, token }
  }

  getToken(): string | null {
    return localStorage.getItem('token')
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  persistUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user))
    const raw = localStorage.getItem('authData')
    if (!raw) return
    try {
      const data = JSON.parse(raw) as { user?: User }
      data.user = user
      localStorage.setItem('authData', JSON.stringify(data))
    } catch {
      // ignore malformed authData
    }
  }

  setAuthData(user: User, token: string): void {
    const authData = {
      user,
      token,
      timestamp: Date.now()
    }
    localStorage.setItem('authData', JSON.stringify(authData))
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('token', token)
  }
}

const authService = new AuthService()
export default authService
